import { createContext, useContext, useState } from 'react';

import { ContextType, TTournamentParams } from '~/../api/common.types';
import { useGameAuthentication } from '~/contextProviders/GameAuthenticationProvider';
import { useGetTournamentOwnerPairings } from '~/hooks/game/useGetOwnerPairings';
import { useGetTournamentByesByOwner } from '~/hooks/game/useGetTournamentByesByOwner';
import { useGetAccount } from '~/hooks/web3/useGetAccount';

import { GameContextProviderTypes } from './GameContextProvider.types';

const GameContext = createContext({} as GameContextProviderTypes);

const GameContextProvider = ({
  children,
  battleId,
  tournamentParam,
  totalStates,
}: {
  children: any;
  battleId: number;
  tournamentParam: TTournamentParams;
  totalStates: ContextType[];
}) => {
  const { data: connectedAccount } = useGetAccount();
  const { accessToken } = useGameAuthentication();

  const [battle, setBattle] = useState(battleId);
  const [playMusic, setPlayMusic] = useState(false);
  const [batchAttack, setBatchAttack] = useState(undefined);
  const [totalBatchAttackSuccess, setTotalBatchAttackSuccess] = useState(false);


  const context = totalStates.find((cont) => cont.state === battle);
  const areResultsReady = context?.resources?.length ?
    context.resources.includes('RESULTS') :
    false;
  const arePairngsOnly = context?.resources?.length === 1 &&
    context.resources.includes('PAIRINGS') && context.is_active === false;
  const isAttacksActive = context?.is_active;

  const { data: pairings, isLoading: isLoadingPairings } =
    useGetTournamentOwnerPairings({
      data: {
        contextId: context?.context_id || '',
        battleId: battle,
        walletAddress: connectedAccount?.address!,
      },
      enabled: !!accessToken && !!context && Boolean(connectedAccount?.address),
    });
  const { data: byesByOwner } = useGetTournamentByesByOwner({
    data: {
      contextId: context?.context_id || '',
      battleId: battle,
      walletAddress: connectedAccount?.address || '',
    },
    enabled: !!connectedAccount?.address && !!context,
  });

  const isLoading = isLoadingPairings;
  // attacks cannot be submitted and results are not published
  const areWaitingForResults = !isAttacksActive && !areResultsReady;
  return (
    <GameContext.Provider
      value={{
        ownerPairings: pairings,
        isLoading: isLoading,
        battle: battle,
        setBattle,
        isAttacksActive: !!isAttacksActive,
        areResultsReady,
        arePairngsOnly,
        areWaitingForResults,
        contextId: context?.context_id || '',
        tournamentParam,
        byesByOwner,
        totalRounds: totalStates.length,
        setPlayMusic,
        playMusic,
        batchAttack,
        setBatchAttack,
        totalBatchAttackSuccess,
        setTotalBatchAttackSuccess,
      }}
    >
      {children}
    </GameContext.Provider>
  );
};

export default GameContextProvider;

export function useGameContext(): GameContextProviderTypes {
  return useContext(GameContext);
}
